import { useState } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faDownload, faXmark } from "@fortawesome/free-solid-svg-icons";
import ThemeToggle from "./ThemeToggle";
import { profile } from "../../data/profile";

const NAV_LINKS = [
  { href: "#about", label: "About" },
  { href: "#skills", label: "Skills" },
  { href: "#work", label: "Work" },
  { href: "#experience", label: "Experience" },
  { href: "#contact", label: "Contact" },
];

function Header({ theme, onToggleTheme }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  return (
    <SiteHeader>
      <NavWrap aria-label="Primary">
        <Brand href="#home" onClick={closeMenu}>
          <span aria-hidden="true">{profile.initials}</span>
          {profile.name}
        </Brand>
        <DesktopNav>
          {NAV_LINKS.map((link) => (
            <a key={link.href} href={link.href}>
              {link.label}
            </a>
          ))}
        </DesktopNav>
        <HeaderActions>
          <ThemeToggle theme={theme} onToggle={onToggleTheme} />
          <ResumeLink href="/resume.pdf" target="_blank" rel="noreferrer">
            <FontAwesomeIcon icon={faDownload} /> Resume
          </ResumeLink>
          <MenuButton
            type="button"
            aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <FontAwesomeIcon icon={menuOpen ? faXmark : faBars} />
          </MenuButton>
        </HeaderActions>
      </NavWrap>
      {menuOpen && (
        <MobileNav id="mobile-nav">
          {NAV_LINKS.map((link) => (
            <a key={link.href} href={link.href} onClick={closeMenu}>
              {link.label}
            </a>
          ))}
        </MobileNav>
      )}
    </SiteHeader>
  );
}

const SiteHeader = styled.header`
  position: sticky;
  top: 0;
  z-index: 20;
  background: color-mix(in srgb, var(--bg) 88%, transparent);
  backdrop-filter: blur(16px);
  border-bottom: 1px solid var(--border);
`;

const NavWrap = styled.nav`
  max-width: 1240px;
  margin: auto;
  min-height: 76px;
  padding: 0 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

const Brand = styled.a`
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -0.04em;
  color: var(--text-primary);
  text-decoration: none;
  white-space: nowrap;
  display: flex;
  align-items: center;

  span {
    display: inline-grid;
    place-items: center;
    width: 29px;
    height: 29px;
    margin-right: 8px;
    background: var(--text-primary);
    color: var(--bg);
    font-size: 0.72rem;
    border-radius: 50%;
    letter-spacing: 0;
  }
`;

const DesktopNav = styled.div`
  display: flex;
  gap: 29px;

  a {
    color: var(--text-secondary);
    text-decoration: none;
    font-size: 0.83rem;
    font-weight: 600;
    transition: color 0.2s ease;

    &:hover {
      color: var(--accent);
    }
  }

  @media (max-width: 800px) {
    display: none;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const ResumeLink = styled.a`
  color: var(--text-primary);
  text-decoration: none;
  font-size: 0.8rem;
  font-weight: 700;
  border-bottom: 1px solid var(--text-primary);
  padding: 4px 0;

  svg {
    margin-right: 6px;
  }

  @media (max-width: 420px) {
    font-size: 0;
    border: 0;
    svg {
      font-size: 1rem;
      margin: 0;
    }
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: 0;
  padding: 5px;
  font-size: 1.15rem;
  color: var(--text-primary);
  cursor: pointer;

  @media (max-width: 800px) {
    display: block;
  }
`;

const MobileNav = styled.div`
  padding: 10px 28px 20px;
  display: grid;
  gap: 13px;
  border-top: 1px solid var(--border);

  a {
    color: var(--text-primary);
    text-decoration: none;
    font-weight: 650;
    font-size: 0.95rem;
  }
`;

export default Header;
